import React, { useState, useEffect, useMemo, useRef } from 'react';
import { searchCollaborators } from '../../../api/collaboratorsApi';
import { notify } from '../../../shared/feedback/notify';
import { casaComBusca } from '../../../shared/texto/busca-sem-acento';
import { TEXTOS_ESCOLHA_COLABORADOR } from '../../../shared/ui/escolha-colaborador';

import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import SearchIcon from '../../../img/search-icon.svg';
import CarAssociado from '../../../img/car-associado.svg';

import './CollaboratorModal.css';

/** Colaborador vinculado (ou a vincular) ao veículo. */
export interface ColaboradorSelecionado {
  id: string;
  fullName: string | null;
}

/**
 * Textos que dependem do contexto de uso (at-53).
 *
 * O modal nasceu exclusivo do veículo e tinha essas frases fixas. Como a janela de pausa
 * automática precisa da MESMA seleção paginada com busca, os textos viraram parâmetro em vez de o
 * componente ser duplicado — os defaults preservam exatamente o que as telas de veículo já exibiam.
 */
export interface CollaboratorModalTextos {
  titulo?: string;
  /** Prefixo da linha de quem já está no conjunto ("Atualmente no veículo"). */
  atuaisPrefixo?: string;
  /** Linha exibida quando o conjunto atual está vazio. */
  atuaisVazio?: string;
  /** Selo ao lado de quem já está no conjunto ("No veículo"). */
  badgeAtual?: string;
  /** Aviso de quem sai do conjunto ao salvar. Recebe a quantidade. */
  avisoRemocao?: (quantidade: number) => string;
  /** at-93: rótulo da linha "não atribuir", no topo da lista. */
  semNinguemRotulo?: string;
  /** at-93: a consequência de escolher a linha "não atribuir", em uma frase. */
  semNinguemDescricao?: string;
}

interface CollaboratorModalProps {
  isOpen: boolean;
  collaborators: any[];
  /**
   * Conjunto DESEJADO de colaboradores do veículo (at-45): o modal é declarativo —
   * ao salvar, o pai compara com os vinculados atuais e adiciona/remove a diferença.
   */
  selectedUsers: ColaboradorSelecionado[];
  setSelectedUsers: (users: ColaboradorSelecionado[]) => void;
  /** Vinculados atuais no veículo (para marcar quem já está e mostrar o que muda). */
  vinculadosAtuais: ColaboradorSelecionado[];
  onConfirm: () => void;
  onCancel: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  isSidebarOpen: boolean;
  totalCollaborators: number;
  loadPage: (page: number) => void;
  /** at-53: sobrescreve as frases do contexto de veículo. Omitido = comportamento original. */
  textos?: CollaboratorModalTextos;
  /**
   * at-53: esconde a opção de limpar a seleção. Na janela de pausa de escopo específico a lista
   * vazia é inválida, então oferecer a ação seria oferecer um caminho que o salvamento recusa.
   *
   * ⚠️ at-93: a ação deixou de ser o botão "Deixar sem colaborador" no rodapé e virou a **linha
   * "Não atribuir" no topo da lista** — esta prop continua governando a exibição dela.
   */
  ocultarLimparSelecao?: boolean;
  /**
   * at-62: ids que PODEM ser selecionados. `null`/ausente = sem restrição.
   *
   * É a lista de PERMITIDOS, e não de bloqueados, de propósito: a lista de colaboradores é
   * PAGINADA, então um conjunto de bloqueados montado a partir da página carregada não alcançaria
   * as páginas seguintes — quem não atende a esteira apareceria liberado na página 2. O conjunto de
   * elegíveis vem completo do backend, e qualquer id fora dele é bloqueado, em qualquer página.
   *
   * Os bloqueados continuam VISÍVEIS, desabilitados e com o motivo ao lado — esconder faria o Chefe
   * procurar um colaborador que existe e não aparece. Já selecionados antes da restrição (vínculo
   * herdado) seguem removíveis: a trava é só para ADICIONAR.
   */
  idsElegiveis?: string[] | null;
  /** Motivo exibido ao lado de quem está bloqueado. */
  motivoDesabilitado?: string;
  /**
   * at-99: quem só é elegível por causa de uma esteira aberta **em paralelo**, e por qual.
   *
   * 🔴 O aviso existe porque a liberação tem um PORQUÊ que não está na tela (pedido do Victor,
   * 10/09/2026): o veículo aparece em Desmontagem e o pintor aparece disponível. Sem dizer que ele
   * entra pela paralela, o Chefe conclui que ele tem a especialidade da etapa — e quando a
   * paralela encerrar, a pessoa sai da lista sem nada explicando.
   *
   * Mapa `id do colaborador -> nomes das esteiras`. Ausente = ninguém nessa condição.
   */
  elegiveisPorParalela?: Record<string, string[]>;
  /**
   * 🔴 A lista COMPLETA de quem pode trabalhar no veículo — quando presente, ela vira o
   * universo da tela (decisão do Victor, 15/09/2026: *"na seleção de colaboradores, só faz
   * sentido mostrar os colaboradores da esteira, e o(s) da(s) paralela(s)"*).
   *
   * Isto **emenda a at-62**, que decidiu manter os inelegíveis visíveis e desabilitados. O que
   * mudou na prática: com a oficina inteira no seletor, o Chefe percorre páginas de pessoas que
   * não pode escolher para achar as duas que pode — e a marca de desabilitado, no meio de uma
   * lista longa, deixa de informar.
   *
   * ⚠️ Vem como LISTA, não como filtro sobre `collaborators`, porque a paginação é do backend: a
   * lista paginada traz 5 de todos, e filtrar dentro da página produziria páginas de 0, 1 e 3
   * itens — com quem interessa numa página que ninguém abre. Aqui a lista é completa (a rota
   * `GET /especialidades/elegiveis` não pagina) e o modal pagina e busca no cliente.
   *
   * ⚠️ Quem já está VINCULADO ao veículo entra no universo mesmo sem ser elegível (vínculo
   * herdado do grandfathering do ADR 0029): sem isso, o Chefe não teria como tirá-lo pela lista.
   * Ele continua bloqueado para ADICIONAR, pelo `idsElegiveis`.
   *
   * `null`/ausente = comportamento de sempre (lista paginada do backend, inelegível desabilitado).
   * É o que mantém a janela de pausa automática e a aba Especialidades intactas.
   */
  colaboradoresElegiveis?: ColaboradorSelecionado[] | null;
  /** Texto do estado vazio quando NENHUM colaborador atende as esteiras do veículo. */
  vazioElegiveisTexto?: string;
}

const CollaboratorModal: React.FC<CollaboratorModalProps> = ({
  isOpen,
  collaborators,
  selectedUsers,
  setSelectedUsers,
  vinculadosAtuais,
  onConfirm,
  onCancel,
  sidebarRef,
  totalCollaborators,
  loadPage,
  textos,
  ocultarLimparSelecao = false,
  idsElegiveis = null,
  motivoDesabilitado = 'Sem a especialidade desta esteira',
  elegiveisPorParalela,
  colaboradoresElegiveis = null,
  vazioElegiveisTexto,
}) => {
  const titulo = textos?.titulo ?? 'Colaboradores do veículo';
  const atuaisPrefixo = textos?.atuaisPrefixo ?? 'Atualmente no veículo';
  const atuaisVazio = textos?.atuaisVazio ?? 'Nenhum colaborador atribuído';
  const badgeAtual = textos?.badgeAtual ?? 'No veículo';
  /**
   * at-93: rótulo único em toda a aplicação (`shared/ui/escolha-colaborador.ts`) — o Chefe decide
   * a mesma coisa aqui e nos modais de esteira.
   *
   * 🔴 Diferente de lá, aqui a opção **não** vem pré-selecionada (decisão do Victor, 07/09/2026):
   * este modal é declarativo e existe para GERIR o conjunto. Abrir com "Não atribuir" marcado
   * faria "abrir e salvar" remover todo mundo, e adicionar uma pessoa passaria a arriscar perder
   * as outras — o oposto do propósito da tela.
   */
  const semNinguemRotulo = textos?.semNinguemRotulo ?? TEXTOS_ESCOLHA_COLABORADOR.naoAtribuir.rotulo;
  const semNinguemDescricao =
    textos?.semNinguemDescricao ?? 'O veículo fica sem nenhum colaborador ao salvar';
  const avisoRemocao =
    textos?.avisoRemocao ??
    ((quantidade: number) =>
      quantidade === 1
        ? '1 colaborador será removido do veículo ao salvar. Se ele tiver atividade em andamento, será pedida uma confirmação.'
        : `${quantidade} colaboradores serão removidos do veículo ao salvar. Se houver atividade em andamento, será pedida uma confirmação.`);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [filteredCollaborators, setFilteredCollaborators] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const pageSize = 5;

  const modalRef = useRef<HTMLDivElement>(null);

  /**
   * Modo "só quem pode trabalhar" (15/09/2026): a lista vem pronta do backend e o modal assume a
   * busca e a paginação. Fora dele, nada muda — a lista continua paginada pelo `loadPage`.
   */
  const listaRestrita = Array.isArray(colaboradoresElegiveis);

  const universoRestrito = useMemo(() => {
    if (!listaRestrita) return [];
    const porId = new Map<string, any>();
    for (const c of colaboradoresElegiveis ?? []) {
      porId.set(c.id, { id: c.id, fullName: c.fullName, username: null });
    }
    // Quem já está no veículo aparece mesmo sem ser elegível — é a única via de removê-lo.
    for (const v of vinculadosAtuais) {
      if (!porId.has(v.id)) porId.set(v.id, { id: v.id, fullName: v.fullName, username: null });
    }
    return Array.from(porId.values()).sort((a, b) =>
      (a.fullName ?? '').localeCompare(b.fullName ?? '', 'pt-BR'),
    );
  }, [listaRestrita, colaboradoresElegiveis, vinculadosAtuais]);

  const universoRestritoFiltrado = useMemo(
    // Sem acento e sem caixa: o backend já buscava assim, e um `includes` cru faria "joao"
    // deixar de achar "João Silva" — o Chefe digitaria o nome certo e a lista diria que não há
    // ninguém, que é a leitura errada mais cara desta tela.
    () => universoRestrito.filter((u) => casaComBusca(u.fullName, searchTerm)),
    [universoRestrito, searchTerm],
  );

  const isSelecionado = (id: string) => selectedUsers.some((u) => u.id === id);

  /** at-62: bloqueado só para ADICIONAR — remover quem já está segue permitido. */
  const isDesabilitado = (id: string) => idsElegiveis !== null && !idsElegiveis.includes(id);

  /**
   * at-99: a frase que explica por que a pessoa está disponível apesar da etapa do veículo.
   *
   * Nome da esteira por extenso, e não "em paralelo" seco: com mais de uma paralela aberta, só o
   * nome diz de qual serviço se trata.
   */
  const avisoDeParalela = (id: string): string | null => {
    const esteiras = elegiveisPorParalela?.[id];
    if (!esteiras || esteiras.length === 0) return null;
    return esteiras.length === 1
      ? `Disponível pela esteira em paralelo: ${esteiras[0]}`
      : `Disponível pelas esteiras em paralelo: ${esteiras.join(', ')}`;
  };

  const toggleColaborador = (user: { id: string; fullName?: string | null }) => {
    if (isSelecionado(user.id)) {
      setSelectedUsers(selectedUsers.filter((u) => u.id !== user.id));
    } else {
      if (isDesabilitado(user.id)) return;
      setSelectedUsers([...selectedUsers, { id: user.id, fullName: user.fullName ?? null }]);
    }
  };

  const handleSearch = async (page: number = 1) => {
    // No modo restrito a filtragem é local e reativa ao que se digita — a lupa e o Enter só
    // existem para não mudar o gesto de quem já usa a tela.
    if (listaRestrita) {
      setCurrentPage(1);
      return;
    }
    if (!searchTerm.trim()) {
      setIsSearching(false);
      setCurrentPage(1);
      loadPage(1);
      return;
    }

    setLoading(true);
    setIsSearching(true);
    setCurrentPage(page);

    try {
      const response = await searchCollaborators(searchTerm, page, pageSize);
      setFilteredCollaborators(response.users || []);
      setTotalSearchResults(response.total);
    } catch (error) {
      // Sem sinal, a busca sempre dizia "nenhum resultado", mesmo com falha.
      setFilteredCollaborators([]);
      notify.errorFrom(
        error,
        'Não foi possível buscar colaboradores. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setLoading(false);
    }
  };

  // Reseta os estados locais na abertura do modal
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      setSearchTerm('');
      setIsSearching(false);
    }
  }, [isOpen]);

  // Atualiza os colaboradores filtrados quando a prop muda (sem resetar a página)
  useEffect(() => {
    if (isOpen && !isSearching) {
      setFilteredCollaborators(collaborators);
    }
  }, [isOpen, collaborators, isSearching]);

  // Dispara o carregamento dos colaboradores ao mudar de página.
  // Intencional: o efeito deve reagir apenas à abertura do modal, à troca de página e à
  // entrada/saída do modo busca. Incluir 'handleSearch' faria a busca ser refeita a cada tecla
  // digitada (a função muda de identidade junto com o termo digitado), quando a regra é buscar
  // somente no Enter ou no clique da lupa. Incluir 'loadPage' (callback do componente pai)
  // tornaria o efeito dependente da identidade da função recebida: como ela atualiza o estado do
  // próprio pai, um pai que não a memorize levaria o carregamento a disparar em loop.
  useEffect(() => {
    // No modo restrito a lista já está em mãos: pedir página ao backend traria a oficina
    // inteira de volta e a resposta não seria usada por ninguém.
    if (isOpen && !listaRestrita) {
      if (isSearching) {
        handleSearch(currentPage);
      } else {
        loadPage(currentPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isSearching, isOpen, listaRestrita]);

  // Termo digitado muda o conjunto: sem voltar à página 1, uma busca feita na página 3 mostraria
  // "Página 3 de 1" e nenhum resultado, com o colaborador procurado na tela que ficou para trás.
  useEffect(() => {
    if (listaRestrita) setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedNode = event.target as Node;
      const isOutsideModal = modalRef.current && !modalRef.current.contains(clickedNode);
      const isOutsideSidebar = sidebarRef?.current && !sidebarRef.current.contains(clickedNode);

      if (isOutsideModal && isOutsideSidebar) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onCancel, sidebarRef]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const totalPages = listaRestrita
    ? Math.max(1, Math.ceil(universoRestritoFiltrado.length / pageSize))
    : isSearching
    ? Math.ceil(totalSearchResults / pageSize)
    : Math.ceil(totalCollaborators / pageSize);

  /** O que a lista renderiza: a página local no modo restrito, a do backend fora dele. */
  const colaboradoresExibidos = listaRestrita
    ? universoRestritoFiltrado.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : filteredCollaborators;

  if (!isOpen) return null;

  const vinculadoAtual = (id: string) => vinculadosAtuais.some((v) => v.id === id);
  const totalRemovidos = vinculadosAtuais.filter((v) => !isSelecionado(v.id)).length;

  return (
    <div className="collaborator-modal-overlay">
      <div className="collaborator-modal-content" ref={modalRef}>
        <div className="LightboxColaborador">
          <form
            className="FormLightboxColaborador"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            >
            <div className="TopoColaborador">
              <div className="AtribuirColaborador">{titulo}</div>
              {vinculadosAtuais.length > 0 ? (
                <div className="current-responsible-subtitle">
                  {vinculadosAtuais.length === 1
                    ? `${atuaisPrefixo}: `
                    : `${atuaisPrefixo} (${vinculadosAtuais.length}): `}
                  <strong>{vinculadosAtuais.map((v) => v.fullName).filter(Boolean).join(', ')}</strong>
                </div>
              ) : (
                <div className="current-responsible-subtitle empty">{atuaisVazio}</div>
              )}

              {/* Conjunto desejado: o que ficará no veículo ao salvar. */}
              {selectedUsers.length > 0 && (
                <div className="colaboradores-chips">
                  {selectedUsers.map((user) => (
                    <span key={user.id} className="colaborador-chip">
                      {user.fullName || 'Colaborador'}
                      <button
                        type="button"
                        className="colaborador-chip-remove"
                        aria-label={`Tirar ${user.fullName || 'colaborador'} da seleção`}
                        onClick={() => toggleColaborador(user)}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {totalRemovidos > 0 && (
                <div className="colaboradores-remocao-aviso">{avisoRemocao(totalRemovidos)}</div>
              )}

              <div className="Frame1000003981">
                <div className="Frame1000003984">
                  <div className="ItemSearch">
                    <img
                      className="imgIcon"
                      style={{ marginTop: 9}}
                      onClick={() => handleSearch()}
                      src={SearchIcon}
                      alt="Ícone de busca"
                      width="20"
                      height="20"
                    />
                  </div>
                  <div className="PesquisePorColaborador">
                    <input
                    style={{marginBottom: 'unset', marginTop: 'unset'}}
                      type="text"
                      className='PesquiseInputColaborador'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Pesquise pelo colaborador"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="lista-colaboradores-container">
              {loading ? (
                <div className="loading-container">
                  <img
                    src={CarAssociado}
                    alt="Car Associado"
                    width="100"
                    height="100"
                    className="loadCar"
                  />
                  <p className="loading-text">Buscando colaboradores...</p>
                </div>
              ) : colaboradoresExibidos.length > 0 ? (
                <div className="collaborator-list">
                  {/* at-93: "Não atribuir" no topo da lista, com a consequência escrita. Era um
                      botão "Deixar sem colaborador" no rodapé, ao lado de Cancelar e Salvar —
                      lido como ação de rodapé, não como uma escolha entre as pessoas. Reusa as
                      classes de item da própria lista, sem CSS novo. */}
                  {!ocultarLimparSelecao && (
                    <div
                      className={`collaborator-item ${selectedUsers.length === 0 ? 'selected' : ''}`}
                      onClick={() => setSelectedUsers([])}
                      title={semNinguemDescricao}
                    >
                      <div className="collaborator-avatar">-</div>
                      <div className="collaborator-info">
                        <span className="collaborator-name">{semNinguemRotulo}</span>
                        <span className="collaborator-username">{semNinguemDescricao}</span>
                      </div>
                      {selectedUsers.length === 0 && (
                        <div className="selected-indicator">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                  )}
                  {colaboradoresExibidos.map((user) => {
                    const initials = user.fullName
                      ? user.fullName
                          .split(' ')
                          .slice(0, 2)
                          .map((n: string) => n[0])
                          .join('')
                          .toUpperCase()
                      : '?';
                    const isSelected = isSelecionado(user.id);
                    // at-62: quem não atende a esteira aparece, mas não pode ser adicionado.
                    const bloqueado = isDesabilitado(user.id) && !isSelected;
                    // at-99: está disponível, mas por causa de uma esteira aberta em paralelo.
                    const porParalela = bloqueado ? null : avisoDeParalela(user.id);

                    return (
                      <div
                        key={user.id}
                        className={`collaborator-item ${isSelected ? 'selected' : ''} ${vinculadoAtual(user.id) ? 'current-responsible' : ''}`}
                        style={bloqueado ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                        title={bloqueado ? motivoDesabilitado : porParalela ?? undefined}
                        onClick={() => toggleColaborador(user)}
                      >
                        <div className="collaborator-avatar">{initials}</div>
                        <div className="collaborator-info">
                          <span className="collaborator-name">
                            {user.fullName}
                            {vinculadoAtual(user.id) && (
                              <span className="current-responsible-badge">{badgeAtual}</span>
                            )}
                            {/* at-99: selo azul, a mesma cor do selo de paralela ao lado da placa
                                — laranja já é "no veículo" e as duas marcas convivem na linha. */}
                            {porParalela && (
                              <span className="collaborator-paralela-badge">Em paralelo</span>
                            )}
                          </span>
                          <span className="collaborator-username">
                            {bloqueado
                              ? motivoDesabilitado
                              : porParalela ?? user.username ?? 'colaborador'}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="selected-indicator">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-results">
                  {/* Estado vazio honesto: com a lista restrita às especialidades, "nenhum
                      colaborador encontrado" mandaria o Chefe procurar alguém que existe e
                      nunca vai aparecer. O que falta aqui é configuração, não busca. */}
                  <p>
                    {listaRestrita && !searchTerm.trim()
                      ? vazioElegiveisTexto ??
                        'Nenhum colaborador tem a especialidade das esteiras deste veículo. Ajuste as Especialidades em Administração do Sistema.'
                      : 'Nenhum colaborador encontrado.'}
                  </p>
                </div>
              )}
            </div>
            <div className="Frame1000003999">
              <div className="CtaBigColaborador cancel" onClick={onCancel}>
                <button className="CustomFileCancel" type="button">Cancelar</button>
              </div>
              {/* at-93: o botão "Deixar sem colaborador" saiu daqui — virou a linha "Não
                  atribuir" no topo da lista, junto das pessoas, onde a escolha acontece. */}
              <div className="CtaBigColaborador save" onClick={onConfirm}>
                <button className="CustomFileSave" type="button">Salvar</button>
              </div>
            </div>
            <div className="PaginaOColaborador">
              <div
                className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={handlePreviousPage}
                style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                <img
                  className="imgIcon"
                  src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
                  alt="Seta para Esquerda"
                  width="24"
                  height="24"
                />
                <div
                  className="Anterior"
                  style={{ color: currentPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
                >
                  Anterior
                </div>
              </div>
              <span className="collaborator-modal-pagination-info">
                Página {currentPage} de {totalPages}
              </span>
              <div
                className={`Frame1000003949 ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={handleNextPage}
                style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                <div
                  className="PrXima"
                  style={{ color: currentPage === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}
                >
                  Próxima
                </div>
                <img
                  className="imgIcon"
                  src={currentPage === totalPages ? ArrowRightGray : ArrowRightOrange}
                  alt="Seta para Direita"
                  width="24"
                  height="24"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CollaboratorModal;
